import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase/client';
import type { Ticket } from '@/types/tickets';

interface TicketTransferModalProps {
  ticket: Ticket;
  onClose: () => void;
  onComplete: () => void;
}

export function TicketTransferModal({ ticket, onClose, onComplete }: TicketTransferModalProps) {
  const [toEmail, setToEmail] = useState('');
  const [toName, setToName] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Validate email
      if (!toEmail || !toEmail.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create transfer request
      const { data, error: transferError } = await supabase
        .from('ticket_transfers')
        .insert({
          ticket_id: ticket.id,
          from_user_id: user.id,
          to_email: toEmail,
          to_name: toName || null,
          reason: reason || null,
          status: 'pending'
        })
        .select()
        .single();

      if (transferError) throw transferError;

      // Send notification email (via Netlify function)
      await fetch('/.netlify/functions/notify-transfer-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transferId: data.id,
          ticketId: ticket.id,
          toEmail,
          toName
        })
      });

      onComplete();
    } catch (err) {
      console.error('Transfer request error:', err);
      setError(err instanceof Error ? err.message : 'Failed to request transfer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-earth-800 rounded-lg max-w-md w-full"
      >
        <div className="p-4 border-b border-earth-700 flex items-center justify-between">
          <h2 className="text-xl font-display text-sand-100">Request Ticket Transfer</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-sand-400 hover:text-sand-200"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-sm text-yellow-400">
              Transfer requests require approval from the experience creator or admin.
            </p>
          </div>

          <p className="text-sand-400 mb-6">
            Transfer <strong>Ticket #{ticket.ticket_number}</strong> to another person.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-sand-300 mb-1">
                Recipient Email *
              </label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                required
                className="w-full px-3 py-2 bg-earth-700 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:ring-2 focus:ring-sand-400 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-sand-300 mb-1">
                Recipient Name (Optional)
              </label>
              <input
                type="text"
                value={toName}
                onChange={(e) => setToName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-3 py-2 bg-earth-700 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:ring-2 focus:ring-sand-400 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-sand-300 mb-1">
                Reason for Transfer (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Unable to attend due to..."
                rows={3}
                className="w-full px-3 py-2 bg-earth-700 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:ring-2 focus:ring-sand-400 focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-earth-700 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !toEmail}
          >
            {isSubmitting ? (
              <>Requesting...</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Request Transfer
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
} 